import { Dialog, Loader, Text, ThemePrepared } from "@fluentui/react-northstar";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { appState } from "../../AppState";
import { getCachedPhoto } from "../../shared/cache/UserPhoto";
import { generateCircleImage } from "../../shared/components/CircleImages";
import GroupChatActivity from "../../shared/components/TicketConversation/components/GroupChatActivity";
import { DisplayLifecycleWithPhasesPrint } from "../../shared/components/TicketDetailInfo/displayLifecyclePhases/DisplayLifecycleWithPhasesPrint";
import { TicketCustomForm } from "../../shared/interfaces/ticket.interface";
import { PrintHeader } from "../PrintHeader";
import { ApprovalPrint } from "./ApprovalPrint";
import { PrintCustomFormAnswers } from "./CustomFormAnswersPrint";
import { LifecyclePrint } from "./LifecyclePrint";
import { TaskPrint } from "./TaskPrint";
import { TicketActivityPrint } from "./TicketActivityPrint";
import { TicketPagePrintDetails } from "./TicketPagePrintDetails";
import { useTicketPagePrint } from "./useTicketPagePrint";

type TicketPagePrintProps = {
  ticketState: {
    ticketId: any;
    isSelectItem: boolean;
    isTicketLoaded: boolean;
  };
  globalTheme: ThemePrepared<any>;
  ticket: Ticket | null;
  isEndUser?: boolean;
};

export const TicketPagePrint: React.FC<TicketPagePrintProps> = ({
  ticketState,
  ticket,
  globalTheme,
  isEndUser = false
}) => {
  const { t } = useTranslation();
  const currentState = appState();

  const {
    ticketActivities,
    phases,
    tasks,
    ticketApprovals,
    loadActivities,
    isLoading,
    loadLifecycleData,
    loadTaskAndApprovals,
    relatedTickets,
    platformUsers,
  } = useTicketPagePrint(ticket);

  const [circleImages, setCircleImages] = useState<any[]>([]);
  const [circleImage, setCircleImage] = useState<any>();
  const [groupChatText, setGroupChatText] = useState('');

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Detect Ctrl+P (Windows/Linux) or Cmd+P (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "p") {
        event.preventDefault(); // ❌ stops browser print dialog
        loadActivities();
        if (ticket.TicketLifecycle) {
          loadLifecycleData();
        } else {
          loadTaskAndApprovals();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [ticket, loadActivities, loadLifecycleData, loadTaskAndApprovals]);

  useEffect(() => {
    showOpenGroupChatWith();
  }, [ticket.GroupChat]);

  const showOpenGroupChatWith = () => {
    if (!ticket.GroupChat?.Members) return;

    const promises = ticket.GroupChat.Members.map(user => {
      const id = user?.AadObjectId ?? user.UserName;
      return getCachedPhoto(id).then(imageUrl =>
        imageUrl && imageUrl.length > 0 ? imageUrl : null
      );
    });

    Promise.all(promises).then(images => {
      const uniqueImages = [...new Set(images)].filter(Boolean).map(String);
      setCircleImages(uniqueImages);
      const groupChatTitle = t('group-chat.open-header');
      let memberString = ticket.GroupChat.Members.map(m => m.FullName).join(', ');
      if (ticket.GroupChat.Members.length > 3) {
        memberString = `${ticket.GroupChat.Members.slice(0, 3).map(m => m.FullName).join(', ')} and ${ticket.GroupChat.Members.length - 3} other(s)`;
      }
      setGroupChatText(groupChatTitle + ' ' + memberString);
    });
  }

  useEffect(() => {
    (async () => {
      if (circleImages.length > 0) {
        const circleImage = await generateCircleImage(circleImages, 3);
        const imgElement = (
          <img
            src={circleImage}
            alt="Group Chat Members"
            style={{ borderRadius: '50%', width: '24px', height: '24px' }}
          />
        );
        setCircleImage(imgElement);
      }
    })();
  }, [circleImages]);

  const parseJsonValue = <T,>(value: unknown, fallback: T): T => {
    if (typeof value !== "string" || value.length === 0) {
      return fallback;
    }

    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  };

  const printableCustomForms =
    (ticket?.CustomForms as TicketCustomForm[] | undefined)?.length > 0
      ? (ticket.CustomForms as TicketCustomForm[])
          .map((customForm) => ({
            formName: customForm.FormName,
            cardAnswerJSON: parseJsonValue(customForm.CardAnswerJson, {}),
            cardTemplateJSON: parseJsonValue(customForm.CardTemplateJson, {
              body: [],
            }),
          }))
          .filter((form) => Array.isArray(form.cardTemplateJSON?.body))
      : [];

  const hasCustomFormAnswers =
    printableCustomForms.length > 0 ||
    Object.keys(currentState?.cardAnswerJSON ?? {}).length !== 0;

  const renderTicketPagePrintDetailsBlock = () => {
    return (
      <TicketPagePrintDetails
        ticket={ticket}
        relatedTickets={relatedTickets}
        isEndUser={isEndUser}
      />
    )
  }

  const renderTicketLifecyclePhasesBlock = () => {
    if (isEndUser || !ticket.TicketLifecycle) {
      return null;
    }

    return (
        <>
          <div
            className="hidden print-block"
            style={{ pageBreakInside: "avoid" }}
          >
            <div className="mt-3 mb-2">
              <Text size="small" weight="semibold" className="print-text-color">
                {t("ticket.ticket-list.lifecycle")}
              </Text>
            </div>
            <DisplayLifecycleWithPhasesPrint
              ticketData={ticket}
              isAnalyst
            />
          </div>
        </>
    )
  }

  const renderCustomFormAnswersBlock = () => {
    if (!hasCustomFormAnswers) {
      return null;
    }

    return (
      <>
        <div className="print-block hidden mt-2 print-text-color">
          <PrintCustomFormAnswers
            customForms={printableCustomForms}
            cardAnswerJSON={currentState.cardAnswerJSON}
            cardTemplateJSON={currentState.cardTemplateJSON}
          />
        </div>
        {(ticketActivities?.length > 0 ||
          ticket.GroupChat ||
          ticket.TicketLifecycle ||
          (!ticket.TicketLifecycle &&
            (tasks?.length > 0 || ticketApprovals?.length > 0))) && (
          <div className="break-page"></div>
        )}
      </>
    )
  }

  const renderGroupChatActivityBlock = () => {
    if (isEndUser) {
      return null;
    }
    return (
      <div className="hidden print-block print-colored mb-2">
        <GroupChatActivity
          groupChat={ticket.GroupChat}
          circleImage={circleImage}
          groupChatText={groupChatText}
          globalTheme={globalTheme}
        />
      </div>
    )
  }

  const renderTicketLifeCyclePrintBlock = () => {
    if (!ticket.TicketLifecycle || isEndUser) {
      return null;
    }

    return (
      <div className="hidden print-block">
        <LifecyclePrint
          phases={phases ?? []}
          lifecycle={ticket.TicketLifecycle}
        />
      </div>
    )
  }

  const renderTaskPrintBlock = () => {
    if (isEndUser || ticket.TicketLifecycle) {
      return null;
    }

    return (
      <div className="hidden print-block">
        <TaskPrint tasks={tasks} />
      </div>
    )
  }

  const renderApprovalPrintBlock = () => {
    if (ticket.TicketLifecycle) {
      return null;
    }

    return (
      <div className="hidden print-block">
        <ApprovalPrint
          ticketApprovals={ticketApprovals}
          currentUserEmail={currentState.currentUserEmail}
        />
      </div>
    )
  }

  return (
    <>
      <Dialog
        content={<Loader label="Loading necessary data for printing..." />}
        open={isLoading}
      />
      <header className="hidden print-block border-b pb-2">
        <PrintHeader
          ticketId={ticketState.ticketId}
          status={ticket.Status?.Value}
        />
      </header>
      {renderTicketPagePrintDetailsBlock()}
      {renderTicketLifecyclePhasesBlock()}
      {renderCustomFormAnswersBlock()}
      {renderGroupChatActivityBlock()}
      
      <TicketActivityPrint activities={ticketActivities} platformUsers={platformUsers} />

      {renderTicketLifeCyclePrintBlock()}
      {renderTaskPrintBlock()}
      {renderApprovalPrintBlock()}
    </>
  );
};
